import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import useUserSelectedColumns from '../../hooks/useUserSelectedColumns';
import ColumnPanel from './ColumnPanel';
import ResultTableRecord from './ResultTableRecord';
import _, { } from "lodash";
import ResultTableRecDetail from "./ResultTableRecDetail";
import { extractSeqIdNumber } from "../../helpers/utils";
import { useSelector } from 'react-redux';
import './ResultTable.css';
import { FaAngleLeft, FaAngleRight, FaArrowLeft, FaSlidersH, FaExchangeAlt } from "react-icons/fa";
import ToggleSwitch from './ToggleSwitch';
import Tooltip from "@material-ui/core/Tooltip";
import { openDetailsInNewPage as openDetailsInNewPageUtil } from './openDetailsInNewPage';
import { FiExternalLink } from "react-icons/fi";
import { highlightColors } from './highlightColors';

const monthName = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const DEFAULT_VIEW_COLUMNS = ["QUERY_ALI_INFO", "SUBJECT_ALI_INFO", "SUBJECT_PS", "ALI_INFO",
    "SUBJECT_P1", "SUBJECT_CT",
    "SUBJECT_SEQIDNOSUMM", "SUBJECT_FAMINFO", "SUBJECT_D1",
    "SUBJECT_PQ", "ORGANISM", "GXGROUP",
    "SUBJECT_GNAME", "SUBJECT_CC"
];
const DEFAULT_FAM_VIEW_COLUMNS = ["SUBJECT_FAMINFO","NBFAMMEMBERS", "SUBJECT_PB", "SUBJECT_XF","SUBJECT_PM"]

const DEFAULT_UFS_VIEW_COLUMNS = ["SUBJECT_FAMINFO","NBFAMMEMBERS","SUBJECT_PS", "SUBJECT_PB", "SUBJECT_XF","SUBJECT_PM", "UFS_DISPLAY", "FAMMEMBERS_IN_CURRENT", "SUBJECT_PJ"];

const DEFAULT_CAS_VIEW_COLUMNS =["SUBJECT_RG", "SUBJECT_RB", "SUBJECT_RC", "SUBJECT_DS", "SUBJECT_RE"];

const CAS_FIELDS = ["SUBJECT_S9", "SUBJECT_S8", "SUBJECT_DS","SUBJECT_RE","SUBJECT_RD","SUBJECT_RG","SUBJECT_RB","SUBJECT_RC","SUBJECT_NT","SUBJECT_BM","SUBJECT_MP","SUBJECT_SX","SUBJECT_CM"];

const ResultTable = forwardRef(({ columns, data, viewCount, displayMode, tableWidth, fieldConfig, filter, annotState,
                                        expandAllDetailPage, expandAllClaims, expandAllAlignments, openItemsView,
                                        fromItemsPage, groupValue1, groupValue2, saveAnnotation, parentAnnotState,
                                        refreshAnnotState, algorithm, isAbWorkflow, editable,
                                        setFtWindowsToOpen, handleMenuCollapse,handleMenuOpen, isCmNoSeq, aiSaveCols,
                                        buildExtendedCmTracks, getExtendedLvaFlagMaps,
                                        includeSubjectSeq, onToggleFullSubject
                                    }, ref) => {

                                        
        // Helper to extract patent number from VT_FT
        const getWordAfterFulldocview = (inputString) => {
            const regex = /fulldocview\/(\w+)/;
            const match = inputString && inputString.match(regex);
            return match ? match[1] : null;
        };

        // Handler to open fulltext window
        const openFtWindow = (thing) => {
            let dataObj = {
                  pn: getWordAfterFulldocview(thing.VT_FT),
                seqIds: extractSeqIdNumber(thing),
                kws: ""
            };
            setFtWindowsToOpen(dataObj);
        };

    const userInfo = useSelector(state => state.setUserInfo);
    const[tableState, setTableState] = useState(annotState);
    const [dragging, setDragging] = useState(()=>false);
    const [newWidth, setNewWidth] = useState(tableWidth);
    // Dedicated re-render signal bumped on every completed column resize so the body
    // (which reads width from the mutable fieldConfig.fieldConfig[col].colwidth and only
    // updates when ResultTable re-renders) always re-aligns with the header — even when
    // the derived table width is unchanged (900-min clamp, or the resized column is not
    // in the widest displayed type) and setNewWidth would otherwise bail the re-render.
    const [, setResizeTick] = useState(0);
    // Tracks the flanking alignment column's content auto-fit: once the user manually resizes
    // it, their width wins (userResized) until a new result set arrives (keyed by `lastData`).
    const flankFitRef = useRef({ userResized: false, lastData: null });
    // Ephemeral (session-only) content-fitted width for the flanking alignment slot. Applied to
    // the alignment column ONLY while showFullSubject is on, and NEVER written to the persisted
    // fieldConfig colwidth — so it cannot overwrite the user's saved standard-alignment width.
    // null = fall back to the column's own colwidth. Cleared when the user manually resizes.
    const [flankWidth, setFlankWidth] = useState(null);
    const [colConfig, setColConfig] = useState(_.cloneDeep(fieldConfig.fieldConfig));
    const toggle = useSelector(state => state.setToggle.toggle);

    // Side panel state
    const [showSidePanel, setShowSidePanel] = useState(false);
    const [selectedDetailItem, setSelectedDetailItem] = useState(null);
    const [selectedDetailIdx, setSelectedDetailIdx] = useState(null);
    const [selectedRowKey, setSelectedRowKey] = useState(null);
    const [sidePanelFocused, setSidePanelFocused] = useState(false);
    const sidePanelContentRef = React.useRef(null);
    const [currentVisibleSection, setCurrentVisibleSection] = useState(null);

    const [showColumnPanel, setShowColumnPanel] = useState(false);
    const [selectedAvailable, setSelectedAvailable] = useState([]);
    const [displayedColumns, setDisplayedColumns] = useState([]);
    const [userSelectedColumns, setUserSelectedColumns] = useUserSelectedColumns();
    const [famSelectedColumns, setFamSelectedColumns] = useState([]);
    const [ufsSelectedColumns, setUfsSelectedColumns] =  useState([]);
    const [subSelectedColumns, setSubSelectedColumns] = useState([]);
    const [docSelectedColumns, setDocSelectedColumns] = useState([]);
    const [casSelectedColumns, setCasSelectedColumns] = useState([]);
    const [flatSelectedColumns, setFlatSelectedColumns] = useState([]);
    const [aliSelectedColumns, setAliSelectedColumns] = useState([]);
    const [dispSwitch, setDispSwitch] = useState(false);
    const showFullSubject = includeSubjectSeq || false; // controlled by Discover.js via prop
    // True for either alignment column id — both map to a single swappable slot
    const isAlignmentSlot = (c) => c === 'RESULT_FALI_FORMATTED' || c === 'RESULT_EXTENDED_ALI';
    // De-dup: when both alignment ids are in the template, keep only the first occurrence
    const renderColumns = useMemo(() => {
        if (!Array.isArray(columns)) return columns;
        if (!columns.includes('RESULT_FALI_FORMATTED') || !columns.includes('RESULT_EXTENDED_ALI')) return columns;
        let kept = false;
        return columns.filter(c => {
            if (c === 'RESULT_FALI_FORMATTED' || c === 'RESULT_EXTENDED_ALI') {
                if (kept) return false;
                kept = true;
            }
            return true;
        });
    }, [columns]);
    // Temp state for column selection panel
    const [tempDisplayedColumns, setTempDisplayedColumns] = useState([]);
    const getGqpatColumns = () => {
        if (!selectedDetailItem || !fieldConfig || !fieldConfig.getFullRecordFields) return { gqpat: [], cas: [] };
        const doc = data && data.docItems && selectedDetailItem.dbkey ? data.docItems[selectedDetailItem.dbkey] : null;
        if (!doc) return { gqpat: [], cas: [] };
        const fields = fieldConfig.getFullRecordFields(selectedDetailItem.type, data.displayMode);
        let gqpat = [];
        let cas = [];
        fields.forEach(col => {
            if (col === 'DOCHEADER') return;
            switch (col) {
                case 'SUBJECT_P1':
                    if (doc[col]) gqpat.push(col);
                    break;
                case 'SUBJECT_PC':
                    if (doc[col] && Object.keys(doc[col]).length > 0) gqpat.push(col);
                    break;
                case 'SUBJECT_CT':
                    if (fieldConfig.displayField(doc, col) && doc[col] && Object.keys(doc[col]).length > 0) gqpat.push(col);
                    break;
                case 'SUBJECT_Z3':
                    if (fieldConfig.displayField(doc, col) && doc[col] && Object.keys(doc[col]).length > 0) gqpat.push(col);
                    break;
                case 'SUBJECT_CC':
                    if(doc[col]) gqpat.push(col);
                    break;
                case 'SUBJECT_SEQIDNOSUMM':
                    if(fieldConfig.displayField(doc, col)) gqpat.push(col);
                    break;
                case 'ASSIGNEES':
                    if(doc['SUBJECT_PA'] || doc['SUBJECT_PU'] || doc['SUBJECT_PZ'] || doc['SUBJECT_PI']) gqpat.push(col);
                    break;
                case 'PATENT_DATES':
                    if(doc['SUBJECT_D3'] || doc['SUBJECT_D4'] || doc['SUBJECT_PR'] || doc['SUBJECT_D5'] || doc['SUBJECT_D2']) gqpat.push(col);
                    break;
                case 'LEGAL_STATUS':
                    if(doc['SUBJECT_PG'] || doc['SUBJECT_PH'] || doc['SUBJECT_L3'] || doc['SUBJECT_L2']) gqpat.push(col);
                    break;
                case 'ORGANISM':
                    if(doc['SUBJECT_OS'] || doc['SUBJECT_OO'] || doc['SUBJECT_OX']) gqpat.push(col);   
                    break;
                case 'QUERY_ALI_INFO':
                case 'SUBJECT_ALI_INFO':
                case 'ALI_INFO':
                    if(doc) gqpat.push(col);
                    break;
                case 'RESULT_EXTENDED_ALI':
                    if (doc[col] || doc['RESULT_FALI_FORMATTED']) gqpat.push(col);
                    break;
                case 'UFS_DISPLAY':
                    if(fieldConfig.displayField(doc, col)) gqpat.push(col);
                    break;
                case 'CAS_RGGROUP':
                    if (doc['SUBJECT_RG'] || doc['SUBJECT_RB'] || doc['SUBJECT_RC']) cas.push(col);
                    break;
                case 'CAS_CDGROUP':
                    if (doc['SUBJECT_CD'] || doc['SUBJECT_CK'] || doc['SUBJECT_CL']) cas.push(col);
                    break;
                case 'GXGROUP':
                    if(doc['SUBJECT_GX'] || doc['UFS_DISPLAY'] || doc['SUBJECT_PJ']) gqpat.push(col);
                    break;
                case 'PCT_GROUP':
                    if(doc['SUBJECT_P4'] || doc['SUBJECT_P5'] || doc['SUBJECT_P8']) gqpat.push(col);
                    break;
                case 'SUBJECT_FAMINFO':
                    if(fieldConfig.displayField(doc, col)) gqpat.push(col);
                    break;
                case 'SUBJECT_ZF':
                case 'SUBJECT_ZG':
                case 'SUBJECT_ZH':
                    // AI Normalized Parent / AI Parent Confidence / AI Parent Reasoning
                    // are excluded from Custom View settings' Available Columns.
                    break;
                default:
                    if (CAS_FIELDS.includes(col)) {
                        if (doc[col]) cas.push(col);
                    } else if (doc[col]) {
                        gqpat.push(col);
                    }
                    break;
            }
        });
        if(viewCount && viewCount.SUBJECT_RG == 0){
              cas = [];
        }
        return { gqpat, cas };
    };

    const [availableColumns, setAvailableColumns] = useState({
        'GQPAT Data': [],
        'CAS Biosequences': [],
    });

   
    useEffect(() => {
        const { gqpat, cas } = getGqpatColumns();
        setAvailableColumns(prev => ({
            ...prev,
            'GQPAT Data': gqpat,
            ...(cas.length > 0 ? { 'CAS Biosequences': cas } : { 'CAS Biosequences': [] })
        }));
    }, [selectedDetailItem, data]);

   
    const handleSlidersClick = () => {
        setTempDisplayedColumns([...displayedColumns]);
        setShowColumnPanel(true);
        setShowSidePanel(true);
    };

    const handlePreviousClick = () => {
        const keys = getAllRowKeys();
         if (!selectedRowKey || keys.length === 0) return;
          let idx = keys.indexOf(selectedRowKey);
        if (idx > 0) {
            while(idx > 0){
                const newKey = keys[idx - 1];
                var newItem = findItemByKey(newKey);
                if (!newItem || newItem.maxed_out) {
                   idx--;
                }
                else {
                    setSelectedDetailItem(newItem);
                    setSelectedDetailIdx(getItemIndex(newKey));
                    setSelectedRowKey(newKey);
                    break;
                }

            }
        }

    };

     const openDetailsInNewPage = () => {
            openDetailsInNewPageUtil(selectedDetailItem, data, fieldConfig, toggle, {
                filter,
                annotState,
                parentAnnotState,
                fromItemsPage,
                groupValue1,
                groupValue2,
                isAbWorkflow,
                isCmNoSeq,
                displayedColumns,
                idx: selectedDetailIdx
            });
        };

    const handleNextClick = () => { 
        const keys = getAllRowKeys();
        if (!selectedRowKey || keys.length === 0) return;
        let idx = keys.indexOf(selectedRowKey);
        if (idx < keys.length - 1) {
            while(idx >= 0 && idx < keys.length - 1){
                const newKey = keys[idx + 1];
                const newItem = findItemByKey(newKey);
                
                if (!newItem || newItem.maxed_out) {
                    idx++;
                } else {
                    setSelectedDetailItem(newItem);
                    setSelectedDetailIdx(getItemIndex(newKey));
                    setSelectedRowKey(newKey);
                    break;
                }
            }

        }
    }
    

    const handleAvailableChange = (e) => {
        const value = Array.from(e.target.selectedOptions, option => option.value);
        setSelectedAvailable(value);
    };

    
    const handleAddColumns = () => {
        const alignmentExclusion = {
            'RESULT_FALI_FORMATTED': 'RESULT_EXTENDED_ALI',
            'RESULT_EXTENDED_ALI': 'RESULT_FALI_FORMATTED'
        };
        setTempDisplayedColumns(prev => {
            // Swap a mutually exclusive alignment column into its counterpart's slot
            // so the display order is kept; everything else is appended at the end
            const next = [...prev];
            const toAppend = [];
            selectedAvailable.forEach(col => {
                if (next.includes(col)) return;
                const counterpartIdx = alignmentExclusion[col] ? next.indexOf(alignmentExclusion[col]) : -1;
                if (counterpartIdx >= 0) {
                    next[counterpartIdx] = col;
                } else {
                    toAppend.push(col);
                }
            });
            return [...next, ...toAppend];
        });
        setSelectedAvailable([]);
    };

    
   const handleRemoveColumns = (cols) => {
    setTempDisplayedColumns(prev => prev.filter(c => !cols.includes(c)));
};

    const handleResetColumns = () => {
        const allAvailable = Object.values(availableColumns).flat();
        let defaultColumns = [];;
        if(displayMode === 'family'){
            defaultColumns = DEFAULT_FAM_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
        }
        else if(displayMode === 'ufs'){
            defaultColumns = DEFAULT_UFS_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
        }
        else{
            defaultColumns = DEFAULT_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
        }
        setTempDisplayedColumns(defaultColumns);
    };

    
    const handleCloseColumnPanel = (closePanel = false) => {
        setShowColumnPanel(false);
        setTempDisplayedColumns([]);
        setSelectedAvailable([]);
    };

    
    const handleCloseColumnPanelApply = () => {
        setDisplayedColumns(tempDisplayedColumns);
        if (tempDisplayedColumns.includes('RESULT_EXTENDED_ALI') && !showFullSubject && onToggleFullSubject) {
            onToggleFullSubject(true);
        }
        if (selectedDetailItem?.type === 'flat' || selectedDetailItem?.type === 'sin') {
            fieldConfig.aiAliconfigs.aliCols = [...tempDisplayedColumns];
            setAliSelectedColumns(fieldConfig.aiAliconfigs.aliCols);
        } else if (displayMode === 'family') {
            fieldConfig.aiAliconfigs.famCols = [
                    ...DEFAULT_FAM_VIEW_COLUMNS,
                    ...tempDisplayedColumns.filter(col => !DEFAULT_FAM_VIEW_COLUMNS.includes(col))
                ];
            setFamSelectedColumns(fieldConfig.aiAliconfigs.famCols);
        } else if (displayMode === 'ufs') {
            fieldConfig.aiAliconfigs.ufsCols = [
                    ...DEFAULT_UFS_VIEW_COLUMNS,
                    ...tempDisplayedColumns.filter(col => !DEFAULT_UFS_VIEW_COLUMNS.includes(col))
                ];
            setUfsSelectedColumns(fieldConfig.aiAliconfigs.ufsCols);
        } else if (displayMode === 'ssv') {
            setSubSelectedColumns(tempDisplayedColumns);
            fieldConfig.aiAliconfigs.subCols = [
                    ...tempDisplayedColumns
                ];
        } else if (displayMode === 'document') {
            setDocSelectedColumns(tempDisplayedColumns);
            fieldConfig.aiAliconfigs.docCols = [
                    ...tempDisplayedColumns
                ];
        }else if (displayMode === 'cas') {
            fieldConfig.aiAliconfigs.casCols = [
                    ...DEFAULT_CAS_VIEW_COLUMNS,
                    ...tempDisplayedColumns.filter(col => !DEFAULT_CAS_VIEW_COLUMNS.includes(col))
                ];
                setCasSelectedColumns(fieldConfig.aiAliconfigs.casCols);
        }else if (displayMode === 'flat') {
            fieldConfig.aiAliconfigs.flatCols = [
                    ...tempDisplayedColumns
                ];
                setFlatSelectedColumns(fieldConfig.aiAliconfigs.flatCols);
        }
         else{
            setUserSelectedColumns(tempDisplayedColumns);
        }
        setDispSwitch(true);
        setShowColumnPanel(false);
        setShowSidePanel(true);
        setTempDisplayedColumns([]);
        setSelectedAvailable([]);
        fieldConfig.saveConfig(fieldConfig, 'default', filter);
        //fieldConfig.loadConfig('default', fieldConfig, filter);
    };

        // Collect all row keys (composite)for navigation (flat only for now)
        const getAllRowKeys = () => {
            if (!data || !data.data) return [];
            let keys = [];
            Object.keys(data.data).forEach(itemKey => {
                keys.push(`level1_${data.data[itemKey].dbkey}`);
                if (data.data[itemKey].nodes) {
                    Object.keys(data.data[itemKey].nodes).forEach(level2Key => {
                        keys.push(`level2_${itemKey}_${data.data[itemKey].nodes[level2Key].dbkey}`);
                        if (data.data[itemKey].nodes[level2Key].nodes) {
                            Object.keys(data.data[itemKey].nodes[level2Key].nodes).forEach(level3Key => {
                                keys.push(`level3_${itemKey}_${level2Key}_${data.data[itemKey].nodes[level2Key].nodes[level3Key].dbkey}`);
                            });
                        }
                    });
                }
            });
            return keys;
        };

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
        openFirstRow: () => {
            //console.log('DEBUG ResultTable: openFirstRow called');
            //console.log('DEBUG ResultTable: data exists?', !!data);
            //console.log('DEBUG ResultTable: data.data exists?', !!(data && data.data));
            const keys = getAllRowKeys();
           // console.log('DEBUG ResultTable: keys found:', keys.length, keys);
            if (keys && keys.length > 0) {
                const firstKey = keys[0];
                //console.log('DEBUG ResultTable: Opening first key:', firstKey);
                toggleFullRecordExpand(firstKey);
            } else {
                //console.log('DEBUG ResultTable: No keys available to open');
            }
        },
        closeSidePanel: () => {
            setShowSidePanel(false);
            setSelectedRowKey(null);
            setTimeout(() => {
                setSelectedDetailItem(null);
                setSelectedDetailIdx(null);
            }, 100);
        
        }
    }), [data]);

    // Keyboard navigation for side panel and scroll logic for side and result table
    useEffect(() => {
        if (!showSidePanel) return;
        const handleKeyDown = (e) => {
            if (sidePanelFocused) {
                // Scroll side panel content
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    const scrollAmount = 40; // px per key press
                    if (sidePanelContentRef.current) {
                        if (e.key === 'ArrowUp') {
                            sidePanelContentRef.current.scrollTop -= scrollAmount;
                        } else if (e.key === 'ArrowDown') {
                            sidePanelContentRef.current.scrollTop += scrollAmount;
                        }
                    }
                }
            } else {
                // Row navigation
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    const keys = getAllRowKeys();
                    if (!selectedRowKey || keys.length === 0) return;
                    let idx = keys.indexOf(selectedRowKey);
                    if (idx === -1) return;
                    let newIdx = idx;
                    if (e.key === 'ArrowUp') {
                        newIdx = Math.max(0, idx - 1);
                    } else if (e.key === 'ArrowDown') {
                        newIdx = Math.min(keys.length - 1, idx + 1); //for last row of the table
                    }
                        if (newIdx !== idx) {
                            let searchIdx = newIdx;
                            let found = false;
                            while (searchIdx >= 0 && searchIdx < keys.length) {
                                const newKey = keys[searchIdx];
                                const newItem = findItemByKey(newKey);
                                if (!newItem || newItem.maxed_out) {
                                    // If ArrowDown, keep moving forward; if ArrowUp, keep moving backward
                                    if (e.key === 'ArrowDown') {
                                        searchIdx++;
                                    } else {
                                        searchIdx--;
                                    }
                                    continue;
                                } else {
                                    setSelectedDetailItem(newItem);
                                    setSelectedDetailIdx(getItemIndex(newKey));
                                    setSelectedRowKey(newKey);
                                    found = true;
                                    break;
                                }
                            }
                        }
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [showSidePanel, selectedRowKey, data, sidePanelFocused]);

    // Extra container width contributed by the ephemeral flanking auto-fit:
    // render the alignment slot at `flankWidth` while estimateTableWidth still counts its
    // persisted colwidth, so the #maintable container must be adjusted by the difference (can be
    // negative). Zero unless a fitted flanking width is active. Kept out of fieldConfig entirely.
    const flankContainerDelta = () => {
        if (!showFullSubject || flankWidth == null) return 0;
        const cols = Array.isArray(renderColumns) ? renderColumns : (columns || []);
        const alignId = cols.find(isAlignmentSlot);
        if (!alignId) return 0;
        return flankWidth - (parseInt(fieldConfig.fieldConfig[alignId]?.colwidth) || 0);
    };

    // Container width needed by the columns actually rendered in the flat view. The template-based
    // estimate can fall short of what the header/body draw (e.g. after an alignment column swap),
    // and the floated cells then wrap onto a second line (alignment/PDF Link under the row).
    const renderedFlatWidth = () => {
        if (displayMode !== 'flat' || !Array.isArray(renderColumns)) return 0;
        let width = 330; // row prefix (checkbox, stars, annotation, caret) + slack, as in estimateTableWidth
        renderColumns.forEach(col => {
            const colWidth = (isAlignmentSlot(col) && showFullSubject && flankWidth != null)
                ? flankWidth
                : (parseInt(fieldConfig.fieldConfig[col]?.colwidth) || 130);
            width += colWidth + 20;
        });
        return width;
    };

    // Never let the container be narrower than the rendered columns
    const tableContainerWidth = (estimatedWidth) => {
        let width = Math.max(estimatedWidth, renderedFlatWidth());
        if (showSidePanel) {
            width += 800;
        }
        return width;
    };

    useEffect(() => {
        // Adjust maintable width, when side panel is open so the result table data underneath the side panel
        // can also be visible when horitally scrolling.
        // Recompute from the live column widths (not the stale `tableWidth` prop, which is
        // only refreshed on fetch) so any column the user has resized this session is
        // reflected and the container stays in sync with the sum of the columns.
        let width = fieldConfig.estimateTableWidth(fieldConfig.userTableTemplate, fieldConfig.tableDisplayMode);
        width += flankContainerDelta();
        setNewWidth(tableContainerWidth(width));
    }, [tableWidth, showSidePanel, showFullSubject, flankWidth, renderColumns, displayMode]);

    // Auto-fit the flanking alignment column to its rendered content, so the full-subject
    // alignment is sized to what it actually shows: a short match no longer leaves a huge
    // fixed-width gap that pushes the other columns off-screen, and a long one isn't clipped
    // to a guess. We measure the widest rendered alignment via a Range (which reports the
    // intrinsic content width regardless of the cell's overflow/box) and set ONE shared
    // colwidth, so the header and every body row stay aligned. Clamped to a readable minimum
    // (fits the header label) and a sane maximum, re-fit on each new result set, and skipped
    // once the user has manually resized the column (their width wins — see resizeColCallback).
    useEffect(() => {
        if (!showFullSubject) return;
        // Reset the manual-resize guard only on a genuinely new result set. Discover replaces
        // searchResultData's TOP-LEVEL ref on view-switch/column-apply (shallow spread), which
        // preserves the nested `.data` ref — so key off `.data` to avoid discarding the user's width.
        const dataKey = (data && data.data) || data;
        if (flankFitRef.current.lastData !== dataKey) {
            flankFitRef.current.userResized = false;
            flankFitRef.current.lastData = dataKey;
        }
        if (flankFitRef.current.userResized) return;
        const MIN = 360, MAX = 900, PAD = 24;
        let cancelled = false;
        let timer = null;
        let attempts = 0;
        const measureAndFit = () => {
            if (cancelled || flankFitRef.current.userResized) return;
            const pres = document.querySelectorAll('.ali-pre-flanking');
            let contentMax = 0;
            let stillLoading = false;
            pres.forEach(el => {
                if (/Loading full subject/i.test(el.textContent || '')) stillLoading = true;
                try {
                    const range = document.createRange();
                    range.selectNodeContents(el);
                    contentMax = Math.max(contentMax, range.getBoundingClientRect().width);
                } catch (e) { /* ignore unmeasurable nodes */ }
            });
            // The full-subject alignment loads asynchronously; if nothing is rendered yet or the
            // "Loading…" placeholder is still showing, retry briefly so the fit lands once it arrives.
            if ((pres.length === 0 || contentMax <= 0 || stillLoading) && attempts < 20) {
                attempts++;
                timer = setTimeout(measureAndFit, 250);
                return;
            }
            if (contentMax <= 0) return;
            const target = Math.min(MAX, Math.max(MIN, Math.round(contentMax + PAD)));
            // Store the fitted width in ephemeral state ONLY (never touch the persisted colwidth).
            // Header, body, and the container delta all read `flankWidth` while flanking is on.
            setFlankWidth(prev => (prev != null && Math.abs(prev - target) <= 2) ? prev : target);
        };
        const raf = requestAnimationFrame(measureAndFit);
        return () => { cancelled = true; cancelAnimationFrame(raf); if (timer) clearTimeout(timer); };
    }, [showFullSubject, data, showSidePanel]);

    const displayItemsView = (dbKey1, dbkey2, parentGroup) => {
        openItemsView(dbKey1, dbkey2, parentGroup, fieldConfig, filter);
        closeSidePanel(); // Close side panel if open when the DiscoveMoreItems modal opens
    }
    const toggleFullRecordExpand = (key) => {
        const item = findItemByKey(key);
        if (item) {
            // If the same item is clicked and panel is open, close it
            if (selectedRowKey === key && showSidePanel) {
                closeSidePanel();
            } else {
                // Different item clicked or panel is closed - open/update panel with new item
                setSelectedDetailItem(item);
                setSelectedDetailIdx(getItemIndex(key));
                setSelectedRowKey(key);
                setShowSidePanel(true);
                if (typeof handleMenuCollapse === "function") {
                    handleMenuCollapse();
                }
            }
        } else {
            //console.log('Item not found for key:', key);
        }
    }

    const findItemByKey = (key) => {
        //console.log('findItemByKey called with key:', key);
        if (!data || !data.data) {
            return null;
        }
        //Creating composite key handling logic, because of multiple row highlight error
        if (typeof key === 'string' && key.includes('_')) {
            const parts = key.split('_');
            const level = parts[0];
            
            // Special case for level1_ali_12 type keys
            if (parts.length === 3 && level === 'level1') {
                const dbkey = parts[1] + '_' + parts[2];
                
                for (let itemKey in data.data) {
                    const item = data.data[itemKey];
                    if (item.dbkey === dbkey) {
                        return item;
                    }
                }
                return null;
            }
            
            let dbkey;
            if (level === 'level1') {
                dbkey = key.substring(7);
            } 
            else if (level === 'level2') {
                const parentKey = parts[1];
                dbkey = parts.slice(2).join('_'); 
            }
            else if (level === 'level3') {
               
                const grandParentKey = parts[1];
                const parentKey = parts[2];
                dbkey = parts.slice(3).join('_');
            }
            else {
                dbkey = parts[parts.length - 1];
                //console.log('Using default dbkey extraction:', dbkey);
            }
            
            if (level === 'level1') {
                for (let itemKey in data.data) {
                    const item = data.data[itemKey];
                    if (item.dbkey === dbkey) {
                        return item;
                    }
                }
            } else if (level === 'level2') {
                for (let itemKey in data.data) {
                    const item = data.data[itemKey];
                    if (item.nodes) {
                        for (let level2Key in item.nodes) {
                            const level2Item = item.nodes[level2Key];
                            if (level2Item.dbkey === dbkey) {
                                return level2Item;
                            }
                        }
                    }
                }
            } else if (level === 'level3') {
                for (let itemKey in data.data) {
                    const item = data.data[itemKey];
                    if (item.nodes) {
                        for (let level2Key in item.nodes) {
                            const level2Item = item.nodes[level2Key];
                            if (level2Item.nodes) {
                                for (let level3Key in level2Item.nodes) {
                                    const level3Item = level2Item.nodes[level3Key];
                                    if (level3Item.dbkey === dbkey) {
                                        return level3Item;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        
        // If it's not a composite key or we didn't find it, fall back to original logic
        // Search in main data
        for (let itemKey in data.data) {
            const item = data.data[itemKey];
            
            if (item.dbkey === key) {
                return item;
            }
            
            // Search in nested nodes
            if (item.nodes) {
                for (let level2Key in item.nodes) {
                    const level2Item = item.nodes[level2Key];
                    
                    if (level2Item.dbkey === key) {
                        return level2Item;
                    }
                    
                    // Search in level 3 nodes
                    if (level2Item.nodes) {
                        for (let level3Key in level2Item.nodes) {
                            const level3Item = level2Item.nodes[level3Key];
                            
                            if (level3Item.dbkey === key) {
                                return level3Item;
                            }
                        }
                    }
                }
            }
        }
        
        return null;
    }

    const getItemIndex = (key) => {
        let index = 0;
        for (let itemKey in data.data) {
            if (data.data[itemKey].dbkey === key) {
                return index;
            }
            index++;
        }
        return 0;
    }

    const closeSidePanel = () => {
        setShowSidePanel(false);
        setSelectedRowKey(null);
        setTimeout(() => {
            setSelectedDetailItem(null);
            setSelectedDetailIdx(null);
        }, 100);
       
        if (typeof handleMenuOpen=== "function") {
            handleMenuOpen();
        }
    }

    const expandAllClaim = (val) => {
        expandAllClaims(val);
        for (let idx in annotState.claimsCollapse) {
            annotState.claimsCollapse[idx] = val;
        }
        setTableState({...annotState});
    }

    const expandClaim = (key, val) => {
        annotState.claimsCollapse[key] = val;
        setTableState({...annotState});
    }

    const showLessClaim = (key, val) => {
        annotState.claimsCollapseLess[key] = val;
        setTableState({...annotState});
    }

    const expandAllAlignment = (val) => {
        expandAllAlignments(val);
        for (let idx in annotState.alignmentsCollapse) {
            annotState.alignmentsCollapse[idx] = val;
        }
        setTableState({...annotState});
    }

    const expandAlignment = (key, val) => {
        annotState.alignmentsCollapse[key] = val;
        setTableState({...annotState});
    }

    const expandAllDetailPages = (inType, val) => {
        let type = inType;
        if (inType === 'flat' || inType === 'sin') {
            type = 'ali';
        }
        expandAllDetailPage(displayMode, type, val);
        for (let idx in annotState.fullRecordExpand) {
            if (idx.startsWith(type)) {
                annotState.fullRecordExpand[idx] = val;
            }
        }
        setTableState({...annotState});
    }

    const highlight = (fieldConfig, filter, uiField, val, addPrefix) => {
        // field is always a real field that is being displayed
        // take care of combination fields like ALL_TEXT
        let fieldInfo, field, filterVals, highlightVals, combinedVals, i, j, z, entry, color, fieldsToProcess = [],
            prefix;

        if (val == null) {
            return '&nbsp;';
        } else if (val === '') {
            return '&nbsp;';
        }

        // fix display of geneseq Family IDs
        if (uiField === 'SUBJECT_PB' && _.endsWith(val, '-GENESEQ')) {
            val = '<span class="not-available">Unknown (GeneSeq)</span>';
        }

        fieldInfo = fieldConfig.getFieldInfo(uiField);
        if (fieldInfo.type == 'string') {
            // Only needs apply highlighting for string type fields, this check supposes to fast the UI
            fieldsToProcess.push(uiField);
            _.each(fieldConfig.combinedFields, function (members, combinationField) {
                if ((filter.state.smartTextFilter && filter.state.smartTextFilter[combinationField] !== undefined)
                    || (filter.state.smartHighlightFilter && filter.state.smartHighlightFilter[combinationField] !== undefined)) {
                    if (members[uiField] !== undefined) {
                        fieldsToProcess.push(combinationField);
                    }
                }
            });
        }

        const buildHighlightPattern = (text, literalOnly) => {
            // * and ? work as wildcards and $ is ignored, but the text is also matched literally (tried first),
            // so values with special characters (e.g. query sequence ID '14?' or 'IgG(1)') still get highlighted
            // auto-added query sequence IDs (literalOnly) are exact names, so they never use wildcards
            const literal = _.escapeRegExp(String(text));
            if (literalOnly) {
                return literal;
            }
            const wildcard = String(text).replace(/\$/g, '').split(/([*?])/).map(part => {
                if (part === '*') {
                    return '\\w*';
                } else if (part === '?') {
                    return '\\w';
                }
                return _.escapeRegExp(part);
            }).join('');
            return (literal === wildcard) ? literal : literal + '|' + wildcard;
        };

        // apply highlighting for all applicable fields
        for (z = 0; z < fieldsToProcess.length; z++) {
            field = fieldsToProcess[z];
            fieldInfo = fieldConfig.getFieldInfo(field);
            filterVals = filter.state.smartTextFilter ? filter.state.smartTextFilter[field] : undefined;
            highlightVals = filter.state.smartHighlightFilter ? filter.state.smartHighlightFilter[field] : undefined;
            if (filterVals !== undefined || highlightVals !== undefined) {
                filterVals = filterVals || [];
                highlightVals = highlightVals || [];
                combinedVals = filterVals.concat(highlightVals);
                // Sort by length and dictionary order desc
                combinedVals.sort(function(a, b) {return (b.text.length - a.text.length) || b.text.toString().localeCompare(a.text);});
                for (i = 0; i < combinedVals.length; i++) {
                    entry = combinedVals[i];
                    if (entry.value == true) {
                        // the checkbox is checked in the UI
                        color = highlightColors[entry.color];
                        if (val instanceof Array) {
                            let aval = Array();
                            for (var line in val) {
                                let lval = val[line];
                                let temp = buildHighlightPattern(entry.text, entry.source === 'querySeqId');
                                // Ignore those words in tags <>
                                lval = lval.replace(new RegExp('(' + temp + ')(?![^<]*>)', 'gi'), '<span class="black-font highlight-' + color + '">$1</span>');
                                aval[line] = lval;
                            }
                            val = aval;
                        } else {
                            let temp = buildHighlightPattern(entry.text, entry.source === 'querySeqId');
                            // Ignore those words in tags <>
                            val = val.replace(new RegExp('(' + temp + ')(?![^<]*>)', 'gi'), '<span class="black-font highlight-' + color + '">$1</span>');
                        }
                    }
                }
            }
        }

        prefix = (addPrefix ? fieldInfo.prefix : '');
        if (val instanceof Array) {
            val = val.join("<br>");
        }
        // Wrap "SEQ ID NO" mentions for styling; when a number (or a hyphenated range
        // like "261-289") immediately follows, include it and tag the number(s) with
        // data-seqid so hover popups (SeqIdAlignmentPopup) can look up the matching
        // alignment for whichever specific number was hovered/clicked.
        val = String(val).replace(
            /(SEQ[^A-Za-z0-9]+ID[^A-Za-z0-9]+NOS?)([^A-Za-z0-9]*)(\d+(?:\s*[-–—]\s*\d+)?)?/gi,
            (match, label, sep, cluster) => {
                if (!cluster) {
                    return `<span class="seq-id-no">${label}</span>${sep}`;
                }
                const nums = cluster.match(/\d+/g);
                if (nums.length > 1) {
                    // range (e.g. "261-289"): style the whole cluster as one unit, but
                    // keep each number separately clickable for its own alignment popup
                    const taggedCluster = cluster.replace(/\d+/g, n => `<span class="seq-id-no" data-seqid="${n}">${n}</span>`);
                    return `<span class="seq-id-no-group">${label}${sep}${taggedCluster}</span>`;
                }
                return `<span class="seq-id-no" data-seqid="${nums[0]}">${label}${sep}${cluster}</span>`;
            }
        );
        return transformDisplayData(fieldConfig, val, fieldInfo.display, prefix);
    }

    const onMouseOver = (event, col) => {
        let element = document.getElementById(col);
        element.style.whiteSpace = 'normal'; // Enable text wrapping when hovering over the cell
      };
      
      const onMouseOut = (event, col) => {
        let element = document.getElementById(col);
        element.style.whiteSpace = 'nowrap'; // Disable text wrapping when mouse moves out
      };

    const onMouseMove = (event, col) => {
        let element = document.getElementById(col);
        let inDragTarget = (event.clientX >= (element.getClientRects()[0].right - 20));
        element.style.cursor = inDragTarget ? 'ew-resize' : 'default';
        element.style.borderRight = inDragTarget ? 'solid 3px #888' : 'none';
    }

    const onMouseDown = (event, col) => {
        let element = document.getElementById(col);
        let inDragTarget = (event.clientX >= (element.getClientRects()[0].right - 20));
        if (inDragTarget && !dragging) {
            setDragging(true);
            const firstPos = event.pageX;

            // this is what gets executed while the column size is being dragged
            const draggingNow = function (ev) {
                // Base the drag on the RENDERED width: while flanking shows an ephemeral fitted
                // width, start from flankWidth (not the persisted colwidth) so the column doesn't
                // jump on grab. Falls through to colwidth for every normal column.
                let oldWidth = (isAlignmentSlot(col) && showFullSubject && flankWidth != null) ? flankWidth : parseInt(fieldConfig.fieldConfig[col].colwidth);
                if (oldWidth < 0 || oldWidth > 1000) {
                    oldWidth = 130;
                }
                let newWid = ev.pageX - firstPos + oldWidth;
                if (newWid !== undefined && newWid >= 30 && newWid < 1000) {
                    element.style.width = newWid + 'px';
                }
            };

            // this is what gets executed when mouse cursor goes back up after dragging
            const stopDragging = function (ev) {
                // Base the drag on the RENDERED width: while flanking shows an ephemeral fitted
                // width, start from flankWidth (not the persisted colwidth) so the column doesn't
                // jump on grab. Falls through to colwidth for every normal column.
                let oldWidth = (isAlignmentSlot(col) && showFullSubject && flankWidth != null) ? flankWidth : parseInt(fieldConfig.fieldConfig[col].colwidth);
                if (isNaN(oldWidth) || oldWidth < 0 || oldWidth > 1000) {
                    oldWidth = 130;
                }
                // Clamp the final width into the valid range and ALWAYS commit it (plus a
                // re-render via resizeColCallback), so an out-of-bounds drag can no longer
                // leave the header imperatively stuck wide with the body never updating.
                let width = ev.pageX - firstPos + oldWidth;
                width = Math.max(30, Math.min(width, 999));
                fieldConfig.fieldConfig[col].colwidth = width;
                // Normalize the imperatively-mutated inline width back to the committed
                // colwidth so the DOM matches even if React bails the style diff.
                element.style.width = width + 'px';
                resizeColCallback(col, width, width - oldWidth);
                setDragging(false);
                document.body.removeEventListener("mousemove", draggingNow);
                document.body.removeEventListener("mouseup", stopDragging);
            };

            // dragging has started, attach events while dragging, and when dragging stops to body
            document.body.addEventListener("mousemove", draggingNow);
            document.body.addEventListener("mouseup", stopDragging);
        }
    }

    const onMouseLeave = (col, fieldConfig, filter) => {
        let element = document.getElementById(col);
        element.style.borderRight = 'none';
        element.style.cursor = 'default';
        if (dragging && colConfig[col].colwidth !== fieldConfig.fieldConfig[col].colwidth) {
            // When the col width is changed, saves it
            //fieldConfig.saveConfig(fieldConfig, 'default', filter);
            setDragging(false);
        }
    }

    const resizeColCallback = (col, width, adjustedWidth) => {
        fieldConfig.fieldConfig[col].colwidth = width;

        // A manual resize of the alignment column wins: honor the user's width via the persisted
        // colwidth (already set above) and drop the ephemeral content auto-fit — and stop
        // re-fitting for this result set. Match any alignment id (the displayed slot is usually
        // RESULT_FALI_FORMATTED, not RESULT_EXTENDED_ALI).
        let clearingFlank = false;
        if (isAlignmentSlot(col)) {
            flankFitRef.current.userResized = true;
            clearingFlank = true;
            setFlankWidth(null);
        }

        // Recompute the full table width from the live column widths so the outer
        // container always matches the sum of the columns. Using `tableWidth + adjustedWidth`
        // here relied on the stale `tableWidth` prop, so resizing a second column dropped the
        // first column's delta and left a gap / misaligned the columns.
        let newTableWidth = fieldConfig.estimateTableWidth(fieldConfig.userTableTemplate, fieldConfig.tableDisplayMode);
        // Keep the ephemeral flanking delta unless we just cleared it (then colwidth is authoritative).
        // When cleared, the width effect re-runs on the flankWidth change with the rendered widths.
        if (!clearingFlank) {
            newTableWidth += flankContainerDelta();
        }
        setNewWidth(tableContainerWidth(newTableWidth));

        // Guarantee a re-render (and a body re-read of the new colwidth) even when
        // newTableWidth is unchanged, so header and body always stay column-aligned.
        setResizeTick(t => t + 1);

        // When the col width is changed & not from the report links, saves it
        if (colConfig[col].colwidth !== width && editable) {
            colConfig[col].colwidth = width;
            fieldConfig.saveConfig(fieldConfig, 'default', filter);
        }
    }

    // formats dates into readable text and adds prefixes, etc.
    // how to format which piece of information is defined in fieldConfig.fieldConfig object
    const transformDisplayData = (fieldConfig, text, format, prefix) => {
        switch (format) {
            case 'YYYYMMDD': // dates
                if (text > 0) {
                    if (text.substr(4) == "0000") {
                        text = text.substr(0, 4);
                    } else if (text.length > 6) { // MM-DD-YYYY
                        text = monthName[parseInt(text.substr(4, 2)) - 1] + '-' + text.substr(6, 2) + '-' + text.substr(0, 4);
                    } else if (text.length > 4) { // MM-YYYY
                        text = monthName[parseInt(text.substr(4, 2)) - 1] + '-' + text.substr(0, 4);
                    } else { // YYYY
                        text = text.substr(0, 4);
                    }
                } else {
                    text = '<span class="not-available">Not Available</span>';
                }
                break;
            case 'percentage': // percentage, add % at the end
                text = text + '%';
                break;
            case 'pre': // for alignments put them between <pre> tags
                text = '<pre>' + text + '<br><br></pre>';
                break;
            case 'SUBJECT_GNAME':
                // convert dbId to description that can be displayed
                text = fieldConfig.getDatabaseName(text);
                break;
        }
        if (prefix != '') {
            text = prefix + ' ' + text;
        }
        if (text == '') {
            text = '&nbsp;';
        }
        return text;
    }

    useEffect(() => {
        if (!showSidePanel || !dispSwitch) return;

        if (selectedDetailItem?.type === 'flat' || selectedDetailItem?.type === 'sin') {
            if (aliSelectedColumns?.length > 0) setDisplayedColumns(aliSelectedColumns);
            return;
        }

        const columnsByMode = {
            family: famSelectedColumns,
            ufs: ufsSelectedColumns,
            ssv: subSelectedColumns,
            cas: casSelectedColumns,
            flat: flatSelectedColumns,
            document: docSelectedColumns,
            default: userSelectedColumns,
        };

        const cols =
            columnsByMode[displayMode] || columnsByMode.default;

        if (cols?.length > 0) {
            setDisplayedColumns(cols);
        }
    }, [selectedDetailItem,dispSwitch,showSidePanel,displayMode,
        aliSelectedColumns,userSelectedColumns,famSelectedColumns,ufsSelectedColumns,subSelectedColumns,
        casSelectedColumns,docSelectedColumns,flatSelectedColumns,]);
     useEffect(() => {
        // Only set defaults if there are no persisted values
        if (!userSelectedColumns || userSelectedColumns.length === 0) {
            setUserSelectedColumns(DEFAULT_VIEW_COLUMNS);
        }
        if (!famSelectedColumns || famSelectedColumns.length === 0) {
            setFamSelectedColumns(DEFAULT_FAM_VIEW_COLUMNS);
        }
    }, []); 

    useEffect(() => {
        if (!fieldConfig?.aiAliconfigs) return;

        const configMap = [
            ['aliCols', setAliSelectedColumns, DEFAULT_VIEW_COLUMNS],
            ['ufsCols', setUfsSelectedColumns, DEFAULT_UFS_VIEW_COLUMNS],
            ['subCols', setSubSelectedColumns, DEFAULT_VIEW_COLUMNS],
            ['docCols', setDocSelectedColumns, DEFAULT_VIEW_COLUMNS],
            ['casCols', setCasSelectedColumns, DEFAULT_VIEW_COLUMNS],
            ['famCols', setFamSelectedColumns, DEFAULT_FAM_VIEW_COLUMNS],
            ['flatCols', setFlatSelectedColumns, DEFAULT_VIEW_COLUMNS],
        ];

        configMap.forEach(([key, setter, defaultCols]) => {
            const cols = fieldConfig.aiAliconfigs[key];
            setter(cols && cols.length > 0 ? cols : defaultCols);
        });
    }, [fieldConfig?.aiAliconfigs]);// Add aiAliconfigs as dependency

useEffect(() => {

  if (userInfo && userInfo.current_user && Object.keys(userInfo.current_user).length !== 0) {
    // SUBJECT_ZA-ZE are visible to everyone; no aiAccess gate required.
    // SUBJECT_ZF/ZG/ZH (AI Normalized Parent/Confidence/Reasoning) are excluded from Custom View settings.
    setAvailableColumns(prev => ({
      ...prev,
      'AI Summary': ['SUBJECT_ZA', 'SUBJECT_ZB', 'SUBJECT_ZC', 'SUBJECT_ZD', 'SUBJECT_ZE'],
    }));
  }
}, [userInfo]);


// Ensure displayedColumns is always set when side panel opens and Default View is active
useEffect(() => {
  if (showSidePanel && !dispSwitch) {
    const allAvailable = Object.values(availableColumns).flat();
    const defaultColumns = DEFAULT_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
    setDisplayedColumns(defaultColumns);
  }
}, [showSidePanel, dispSwitch, availableColumns, selectedDetailItem]);

    return (
        <div 
            className={toggle ? 'light-mode' : 'dark-mode'}
            style={{
                '--table-z-index': showSidePanel ? '1001' : 'auto'
            }}
        >
            <table style={{ 
                position: 'relative', 
                zIndex: showSidePanel ? 1001 : 'auto' 
            }}>
                <tbody className="resultTable-tbody">
                    <tr>
                        <td 
                            id="maincontent" 
                            className="main-content"
                            style={{ 
                                position: 'relative', 
                                zIndex: showSidePanel ? 1001 : 'auto' 
                            }}
                        >
                            <div 
                                id="maintable" 
                                className={'main-table' + ' ' + (displayMode === 'flat' ? 'main-row-sep' : '')} 
                                style={{
                                    ...(toggle ? { width: newWidth + 'px', color: 'rgb(41 41 41 / 66%)' } : { width: newWidth + 'px', color: '#ccc' }),
                                    position: 'relative',
                                    zIndex: showSidePanel ? 1001 : 'auto', ...(toggle ? {}:{scrollbarColor: '#777 #292929'})
                            }}
                        >
                            {displayMode === 'flat' &&
                                <div className="clearfix row-padding table-header-sticky" 
                                     style={{
                                         backgroundColor: toggle ? '#fff' : '#333',
                                         borderBottom: toggle ? '1px solid #ddd' : '1px solid #555'
                                     }}>
                                    <div className="pull-left" style={{width: '135px'}}>&nbsp;</div>
                                    {
                                        renderColumns.map(
                                            col => (
                                                <React.Fragment key={col}>
                                                <div className="pull-left gqcol table-header" id={col}
                                                     onMouseMove={(e) => onMouseMove(e, col)}
                                                     onMouseDown={(e) => onMouseDown(e, col)}
                                                     onMouseOut={(e) => onMouseOut(e,col)}
                                                     onMouseLeave={() => onMouseLeave(col, fieldConfig, filter)}
                                                     onMouseOver={(e) => onMouseOver(e, col)}
                                                     style = {{width: (isAlignmentSlot(col) && showFullSubject && flankWidth != null ? flankWidth : fieldConfig.fieldConfig[col].colwidth) + "px"}}>
                                                    <div style={toggle ? {color: '#777'}: {color: 'white'}}>
                                                        {isAlignmentSlot(col) ? (
                                                            <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '100%'}}>
                                                                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0}}>{showFullSubject ? 'GenomeQuest Alignment (Flanking Regions)' : 'GenomeQuest Alignment'}</span>
                                                                <Tooltip title={showFullSubject ? 'Show standard alignment' : 'Show full subject sequence'}>
                                                                    <span
                                                                        className="alignment-swap-icon"
                                                                        style={{flexShrink: 0}}
                                                                        onClick={(e) => { e.stopPropagation(); onToggleFullSubject && onToggleFullSubject(!showFullSubject); }}
                                                                    >
                                                                        <FaExchangeAlt style={{fontSize: '11px'}} />
                                                                    </span>
                                                                </Tooltip>
                                                            </span>
                                                        ) : (
                                                            <span dangerouslySetInnerHTML={{__html: fieldConfig.getFieldName(col, algorithm, isAbWorkflow)}}></span>
                                                        )}
                                                    </div>
                                                </div>
                                                </React.Fragment>
                                            )
                                        )
                                    }
                                </div>
                            }
                            {(data.data == undefined || Object.keys(data.data).length == 0) &&
                                <div className="no-results pull-left">
                                There are no results to display.
                                </div>
                            }
                            {data.data && Object.keys(data.data).length > 0 &&
                                Object.keys(data.data).map(
                                    (item, idx) => (
                                        <div key={'div' + idx} className={displayMode === 'flat' ? '' : 'main-row-sep'}>
                                            {idx === 0 &&
                                                <span id="level1"></span>
                                            }
                                          <ResultTableRecord data={data} item={data.data[item]} idx={idx} fieldConfig={fieldConfig} annotState={annotState}
                                                             filter={filter} highlight={highlight} toggleFullRecordExpand={toggleFullRecordExpand}
                                                             selectedRowKey={selectedRowKey} refreshAnnotState={refreshAnnotState}
                                                             rowUniqueKey={`level1_${data.data[item].dbkey}`}
                                                             columns={displayMode === 'flat' ? renderColumns : fieldConfig.userTableTemplate[data.data[item].type]}
                                                             fromItemsPage={fromItemsPage} parentAnnotState={parentAnnotState} resizeColCallback={resizeColCallback}
                                                             groupValue1={groupValue1} groupValue2={groupValue2} saveAnnotation={saveAnnotation}
                                                             setFtWindowsToOpen ={(value)=>{setFtWindowsToOpen(value)}}
                                                             showFullSubject={showFullSubject}
                                                             flankWidth={flankWidth}
                                                             onToggleFullSubject={onToggleFullSubject}
                                                             isCmNoSeq={isCmNoSeq}
                                                             buildExtendedCmTracks={buildExtendedCmTracks}
                                                             getExtendedLvaFlagMaps={getExtendedLvaFlagMaps}
                                                             />

                                                      {data.data[item].nodes && Object.keys(data.data[item].nodes).length > 0 &&
                                                          Object.keys(data.data[item].nodes).map(
                                                              (level2Item, level2Idx) => (
                                                                  <div key={'level2div' + level2Idx} className={`indent tree ${toggle ? 'light-mode' : 'dark-mode'}`}>
                                                                    {level2Idx === 0 &&
                                                      <span id="level2"></span>
                                                  }
                                                  {data.data[item].nodes[level2Item].maxed_out &&
                                                      <div className="row-padding"><a href="#" onClick={(e) => {displayItemsView(data.data[item].nodes[level2Item].maxed_key, '', data.data[item]);
                                                          e.preventDefault();}}>{data.data[item].nodes[level2Item].maxed_out}</a></div>
                                                  }
                                                  {!data.data[item].nodes[level2Item].maxed_out &&                                                                          
                                                  <ResultTableRecord data={data} item={data.data[item].nodes[level2Item]} idx={level2Idx} fieldConfig={fieldConfig} annotState={annotState} filter={filter}
                                                                                         highlight={highlight} toggleFullRecordExpand={toggleFullRecordExpand}
                                                                                         selectedRowKey={selectedRowKey} refreshAnnotState={refreshAnnotState}
                                                                                         rowUniqueKey={`level2_${item}_${data.data[item].nodes[level2Item].dbkey}`}
                                                                                         columns={fieldConfig.userTableTemplate[data.data[item].nodes[level2Item].type]}
                                                                                         fromItemsPage={fromItemsPage} parentAnnotState={parentAnnotState} resizeColCallback={resizeColCallback}
                                                                                         groupValue1={groupValue1} groupValue2={groupValue2} saveAnnotation={saveAnnotation}
                                                                                         setFtWindowsToOpen ={(value)=>{setFtWindowsToOpen(value)}}
                                                                                         showFullSubject={showFullSubject}
                                                                                         flankWidth={flankWidth}
                                                                                         onToggleFullSubject={onToggleFullSubject}
                                                                                         isCmNoSeq={isCmNoSeq}
                                                                                         buildExtendedCmTracks={buildExtendedCmTracks}
                                                                                         getExtendedLvaFlagMaps={getExtendedLvaFlagMaps}
                                                                                         />
                                                }

                                                                      {data.data[item].nodes[level2Item].nodes && Object.keys(data.data[item].nodes[level2Item].nodes).length > 0 &&
                                                                          Object.keys(data.data[item].nodes[level2Item].nodes).map(
                                                                              (level3Item, level3Idx) => (
                                                                                  <div key={'level3div' + level3Idx} className={`indent tree ${toggle ? 'light-mode' : 'dark-mode'}`}>
                                                                                     {level3Idx === 0 &&
                                                                          <span id="level3"></span>
                                                                      }
                                                                      {data.data[item].nodes[level2Item].nodes[level3Item].maxed_out &&
                                                                          <div className="row-padding"><a href="#" onClick={(e) => {
                                                                              displayItemsView(data.data[item].dbkey, data.data[item].nodes[level2Item].nodes[level3Item].maxed_key);
                                                                              e.preventDefault();}}>{data.data[item].nodes[level2Item].nodes[level3Item].maxed_out}</a></div>
                                                                      }
                                                                      {!data.data[item].nodes[level2Item].nodes[level3Item].maxed_out &&                                                                                         
                                                                       <ResultTableRecord data={data} item={data.data[item].nodes[level2Item].nodes[level3Item]} idx={level3Idx}
                                                                                                             fieldConfig={fieldConfig} annotState={annotState} filter={filter}
                                                                                                             highlight={highlight} toggleFullRecordExpand={toggleFullRecordExpand}
                                                                                                             selectedRowKey={selectedRowKey} refreshAnnotState={refreshAnnotState}
                                                                                                             rowUniqueKey={`level3_${item}_${level2Item}_${data.data[item].nodes[level2Item].nodes[level3Item].dbkey}`}
                                                                                                             columns={fieldConfig.userTableTemplate[data.data[item].nodes[level2Item].nodes[level3Item].type]}
                                                                                                             fromItemsPage={fromItemsPage} parentAnnotState={parentAnnotState} resizeColCallback={resizeColCallback}
                                                                                                             groupValue1={groupValue1} groupValue2={groupValue2} saveAnnotation={saveAnnotation}
                                                                                                             setFtWindowsToOpen ={(value)=>{setFtWindowsToOpen(value)}}
                                                                                                             showFullSubject={showFullSubject}
                                                                                                             flankWidth={flankWidth}
                                                                                                             onToggleFullSubject={onToggleFullSubject}
                                                                                                             isCmNoSeq={isCmNoSeq}
                                                                                                             buildExtendedCmTracks={buildExtendedCmTracks}
                                                                                                             getExtendedLvaFlagMaps={getExtendedLvaFlagMaps}
                                                                                                             />
                                                                    }
                                                                                      </div>
                                                                                  )
                                                                              )
                                                                          }
                                                                      </div>
                                                                  )
                                                              )
                                                          }
                                                      </div>
                                                  )
                                              )
                                          }
                                      </div>
                                  </td>
                              </tr>
                          </tbody>
                      </table>

                                            {/* Side Panel */}
                                            {showSidePanel && (
                                                <>
                                                    <div 
                                                        className="side-panel-backdrop"
                                                        onClick={closeSidePanel}
                                                        style={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100vw',
                                                            height: '100vh',
                                                            backgroundColor: 'transparent',
                                                            zIndex: 999,
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                    <div 
                                                        className={`side-panel-overlay ${showSidePanel ? 'open' : ''} ${toggle ? 'light-mode' : 'dark-mode'}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            right: 0,
                                                            width: '51%',
                                                            height: '100vh',
                                                            boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.3)',
                                                            zIndex: 1002,
                                                            transform: showSidePanel ? 'translateX(0)' : 'translateX(100%)',
                                                            transition: 'transform 0.3s ease-in-out',
                                                            overflowY: 'hidden',
                                                        }}
                                                    >
                                                        {/* Side Panel Header */}
                                                        <div
                                                            className="side-panel-header"
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '16px 24px 12px 24px',
                                                                borderBottom: toggle ? '1px solid #eee' : '1px solid #444',
                                                                background: toggle ? '#f9f9f9' : '#222',
                                                                position: 'sticky',
                                                                top: 0,
                                                                zIndex: 20
                                                            }}
                                                        >
                                                            <div style={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' ,gap: '1rem'}}>
                                                                
                                                                {dispSwitch && !showColumnPanel && (
                                                                    <Tooltip title="Customize Columns">
                                                                        <span><FaSlidersH style={{ marginRight: '10px', cursor: 'pointer' }} onClick={handleSlidersClick} /></span>
                                                                    </Tooltip>
                                                                )}
                                                                {showColumnPanel && (
                                                                                <FaArrowLeft className='arrow-icon' onClick={handleCloseColumnPanel} title="Back" />
                                                                            )}
                                                                {!showColumnPanel && (
                                                                    <ToggleSwitch
                                                                        options={['Default View', 'Custom']}
                                                                        value={dispSwitch ? 'Custom' : 'Default View'}
                                                                        //disabledOptions={['doc', 'flat'].includes(selectedDetailItem?.type) ? [] : ['Custom']}
                                                                        onChange={(selected) => {
                                                                            const isCustom = selected === 'Custom';
                                                                            setDispSwitch(isCustom);

                                            const allAvailable = Object.values(availableColumns).flat();
                                            let defaultColumns = [];
                                             if (displayMode === 'family') {
                                                defaultColumns = DEFAULT_FAM_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
                                            }
                                            else {
                                                defaultColumns = DEFAULT_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
                                            }


                                            if (isCustom) {

                                                // Use user selected columns if they exist, otherwise use default columns
                                                let columnsToUse = [];
                                                if (selectedDetailItem?.type === 'flat' || selectedDetailItem?.type === 'sin') {
                                                    columnsToUse = (aliSelectedColumns && aliSelectedColumns.length > 0)
                                                        ? aliSelectedColumns
                                                        : defaultColumns;
                                                } else if (displayMode === 'family') {
                                                    columnsToUse = (famSelectedColumns && famSelectedColumns.length > 0)
                                                        ? famSelectedColumns
                                                        : defaultColumns;
                                                } 
                                                 else if (displayMode === 'ufs') {
                                                    columnsToUse = (ufsSelectedColumns && ufsSelectedColumns.length > 0)
                                                        ? ufsSelectedColumns
                                                        : defaultColumns;
                                                }
                                                else if (displayMode === 'ssv') {
                                                    columnsToUse = (subSelectedColumns && subSelectedColumns.length > 0)
                                                        ? subSelectedColumns
                                                        : defaultColumns;
                                                }
                                                else if (displayMode === 'document') {
                                                    columnsToUse = (docSelectedColumns && docSelectedColumns.length > 0)
                                                        ? docSelectedColumns
                                                        : defaultColumns;
                                                }
                                                else if (displayMode === 'cas') {
                                                    columnsToUse = (casSelectedColumns && casSelectedColumns.length > 0)
                                                        ? casSelectedColumns
                                                        : defaultColumns;
                                                }
                                                else if (displayMode === 'flat') {
                                                    columnsToUse = (flatSelectedColumns && flatSelectedColumns.length > 0)
                                                        ? flatSelectedColumns
                                                        : defaultColumns;
                                                }
                                                else {
                                                    columnsToUse = (userSelectedColumns && userSelectedColumns.length > 0)
                                                        ? userSelectedColumns
                                                        : defaultColumns;
                                                }
                                                setDisplayedColumns(columnsToUse);
                                            } else {
                                                setDisplayedColumns([]);
                                            }
                                        }}
                                    />
                                )}
                                
                                <span style={{
                                    fontSize: '1.5rem', color: toggle ? '#7F7F7F' : '#fff', flex: '1 1 auto', textAlign: 'center', justifyContent: 'center',
                                    alignItems: 'center', display: 'flex', gap: '1rem',
                                }}>
                                    {!showColumnPanel && (<>
                                        <Tooltip title="Previous">
                                            <span>
                                                <FaAngleLeft className='arrow-icon' style={{ color: toggle ? '#7F7F7F' : '#fff', backgroundColor: toggle ? '#eee' : '#333' }} onClick={handlePreviousClick} disabled={!selectedRowKey || getAllRowKeys().indexOf(selectedRowKey) <= 0} />
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Next">
                                            <span>
                                                <FaAngleRight className='arrow-icon' style={{ color: toggle ? '#7F7F7F' : '#fff', backgroundColor: toggle ? '#eee' : '#333' }} onClick={handleNextClick} disabled={!selectedRowKey || getAllRowKeys().indexOf(selectedRowKey) >= getAllRowKeys().length - 1} />
                                            </span>
                                        </Tooltip>
                                    </>)}

                                </span>
                                <span style={{
                                    fontSize: '1.5rem', color: toggle ? '#7F7F7F' : '#fff', flex: '1 1 auto', textAlign: 'center', justifyContent: 'center',
                                    alignItems: 'center', display: 'flex', gap: '1rem',
                                }}>
                                         <Tooltip title="Open Details in new  page">
                                          <span>
                                             <FiExternalLink className='ft-icon'style={{ marginLeft: '10px' }} onClick={() => { openDetailsInNewPage();}} />
                                            </span>
                                         </Tooltip>
                                 </span>

                                                                    
                                                                
                                                                {/* Close Button */}
                                                                <button 
                                                                    onClick={showColumnPanel ? handleCloseColumnPanel : closeSidePanel}
                                                                    className='close-arrow'
                                                                >
                                                                    &times;
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`side-panel-content ${toggle ? 'light-mode' : 'dark-mode'}`}
                                                            
                                                            tabIndex={0}
                                                            ref={sidePanelContentRef}
                                                            onFocus={() => setSidePanelFocused(true)}
                                                            onBlur={() => setSidePanelFocused(false)}
                                                            style={{
                                                                height: 'calc(100vh - 80px)',
                                                                overflowY: 'auto',
                                                                overflowX: 'hidden'
                                                            }}
                                                        >
                                                            {showColumnPanel ? (
                                                                <ColumnPanel
                                                                    availableColumns={availableColumns}
                                                                    selectedAvailable={selectedAvailable}
                                                                    tempDisplayedColumns={tempDisplayedColumns}
                                                                    displayMode={data.displayMode}
                                                                    onAvailableChange={handleAvailableChange}
                                                                    onAddColumns={handleAddColumns}
                                                                    onRemoveColumns={handleRemoveColumns}
                                                                    onReset={handleResetColumns}
                                                                    onMoveUp={(idx) => {
                                                                        if (idx > 0) {
                                                                            setTempDisplayedColumns(prev => {
                                                                                const arr = [...prev];
                                                                                [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                                                                                return arr;
                                                                            });
                                                                        }
                                                                    }}
                                                                    onMoveDown={(idx) => {
                                                                        setTempDisplayedColumns(prev => {
                                                                            if (idx < prev.length - 1) {
                                                                                const arr = [...prev];
                                                                                [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                                                                                return arr;
                                                                            }
                                                                            return prev;
                                                                        });
                                                                    }}
                                                                    onApply={handleCloseColumnPanelApply}
                                                                    onCancel={handleCloseColumnPanel}
                                                                    toggle={toggle}
                                                                    viewType={dispSwitch ? "custom" : "default"}
                                                                />
                                                            ) : selectedDetailItem ? (
                                                                <div key={selectedDetailItem.dbkey}>
                                                                    <ResultTableRecDetail 
                                                                        key={selectedDetailItem.dbkey} 
                                                                        data={data} 
                                                                        item={selectedDetailItem} 
                                                                        idx={selectedDetailIdx} 
                                                                        fieldConfig={fieldConfig} 
                                                                        annotState={annotState} 
                                                                        filter={filter}
                                                                        highlight={highlight} 
                                                                        toggleFullRecordExpand={closeSidePanel} 
                                                                        expandAllDetailPage={expandAllDetailPages} 
                                                                        refreshAnnotState={refreshAnnotState}
                                                                        expandAllAlignments={expandAllAlignment} 
                                                                        expandAllClaims={expandAllClaim} 
                                                                        expandClaim={expandClaim} 
                                                                        expandAlignment={expandAlignment} 
                                                                        showLessClaim={showLessClaim}
                                                                        fromItemsPage={fromItemsPage} 
                                                                        parentAnnotState={parentAnnotState} 
                                                                        groupValue1={groupValue1} 
                                                                        groupValue2={groupValue2} 
                                                                        saveAnnotation={saveAnnotation} 
                                                                        isAbWorkflow={isAbWorkflow}
                                                                        setFtWindowsToOpen={(value)=>{setFtWindowsToOpen(value)}}
                                                                        displayedColumns={displayedColumns}
                                                                        currentVisibleSection={currentVisibleSection}
                                                                        setCurrentVisibleSection={setCurrentVisibleSection}
                                                                        isCmNoSeq={isCmNoSeq}
                                                                        buildExtendedCmTracks={buildExtendedCmTracks}
                                                                        getExtendedLvaFlagMaps={getExtendedLvaFlagMaps}
                                                                        selectedQueryId={(() => {
                                                                        // Find the QUERY_ID for the selected row
                                                                        if (!selectedRowKey) return null;
                                                                        return selectedDetailItem && selectedDetailItem.QUERY_ID ? selectedDetailItem.QUERY_ID : null;
                                                                         })()}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div style={{ 
                                                                    textAlign: 'center', 
                                                                    color: '#999', 
                                                                    marginTop: '50px',
                                                                    fontSize: '16px'
                                                                }}>
                                                                    No record selected
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                  </div>
              )
          });

export default ResultTable;
